import React, { useState, useRef } from "react";
import { Mail, Phone, MapPin, Send, Linkedin, Twitter, Github, CheckCircle2, ArrowRight, Clock, MessageSquare, Headphones } from "lucide-react";
import PwaPromoBanner from "@/components/PwaPromoBanner";
import { api } from "@/lib/api";

/* ── tiny hook: animate on mount ── */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  React.useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

/* ── contact info items ── */
const INFO = [
  {
    icon: <Mail className="w-5 h-5" />,
    label: "Email us",
    value: "queries@jobdew.com",
    sub: "We reply within 24 hours",
    href: "mailto:queries@jobdew.com",
    color: "#000000",
  },
  {
    icon: <Phone className="w-5 h-5" />,
    label: "Call us",
    value: "+91 8147751238",
    sub: "Mon–Fri, 9 AM – 6 PM IST",
    href: "tel:+917411298777",
    color: "#000000",
  },
  {
    icon: <MapPin className="w-5 h-5" />,
    label: "Visit us",
    value: "Shivbasavnagar",
    sub: "Belgaum Karnataka",
    href: "https://maps.google.com",
    color: "#000000",
  },
];

const TOPICS = ["General Inquiry", "Employer Support", "Job Seeker Help", "Billing", "Partnership", "Press / Media", "Bug Report"];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [faqs, setFaqs] = useState([]);

  const heroReveal = useReveal();
  const formReveal = useReveal();
  const faqReveal = useReveal();

  React.useEffect(() => {
    api.get("/faqs").then(r => setFaqs(r.data)).catch(() => setFaqs([]));
  }, []);

  const faqsByCategory = faqs.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

        .cpage { font-family: 'DM Sans', sans-serif; background: #f8fafc; min-height: 100vh; }

        /* HERO */
        .hero {
          background: #000000;
          padding: 80px 24px 96px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .hero::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 70% 60% at 50% 0%, rgba(29,111,242,0.25) 0%, transparent 70%);
          pointer-events: none;
        }
        .hero-dot-grid {
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: 28px 28px;
          pointer-events: none;
        }
        .hero-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(29,111,242,0.15);
          border: 1px solid rgba(29,111,242,0.35);
          color: #93c5fd;
          padding: 6px 16px;
          border-radius: 99px;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 24px;
          letter-spacing: 0.02em;
        }
        .hero h1 {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-weight: 800;
          font-size: clamp(36px, 6vw, 72px);
          color: #fff;
          line-height: 1.08;
          margin: 0 0 20px;
          letter-spacing: -0.03em;
        }
        .hero h1 span { color: #808080; }
        .hero p {
          color: #94a3b8;
          font-size: clamp(15px, 2vw, 18px);
          max-width: 520px;
          margin: 0 auto 48px;
          line-height: 1.65;
          font-weight: 300;
        }

        /* STAT PILLS */
        .stat-row {
          display: flex; flex-wrap: wrap; justify-content: center; gap: 16px;
          position: relative; z-index: 1;
        }
        .stat-pill {
          display: flex; align-items: center; gap: 10px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          padding: 12px 20px;
          backdrop-filter: blur(8px);
          color: #e2e8f0;
          font-size: 14px;
          font-weight: 400;
        }
        .stat-pill strong { font-size: 18px; font-weight: 700; color: #fff; }
        .stat-pill .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

        /* MAIN CONTENT */
        .main-grid {
          max-width: 1100px;
          margin: -40px auto 0;
          padding: 0 24px 80px;
          display: grid;
          grid-template-columns: 1fr 1.55fr;
          gap: 28px;
          align-items: start;
          position: relative; z-index: 2;
        }
        @media (max-width: 768px) {
          .main-grid { grid-template-columns: 1fr; margin-top: -24px; }
        }

        /* INFO CARDS COLUMN */
        .info-col { display: flex; flex-direction: column; gap: 16px; }
        .info-card {
          background: #fff;
          border-radius: 16px;
          padding: 22px 24px;
          border: 1px solid #e2e8f0;
          display: flex; align-items: flex-start; gap: 16px;
          text-decoration: none;
          transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }
        .info-card:hover {
          box-shadow: 0 8px 28px rgba(0,0,0,0.10);
          transform: translateY(-2px);
          border-color: #bfdbfe;
        }
        .info-icon {
          width: 44px; height: 44px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          color: #fff;
        }
        .info-label { font-size: 12px; font-weight: 500; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px; }
        .info-value { font-size: 16px; font-weight: 600; color: #0f172a; font-family: 'Bricolage Grotesque', sans-serif; }
        .info-sub { font-size: 13px; color: #64748b; margin-top: 2px; }

        /* HOURS CARD */
        .hours-card {
          background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #1e3a5f;
          color: #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,0.1);
        }
        .hours-card h3 { font-family: 'Bricolage Grotesque', sans-serif; font-size: 17px; font-weight: 700; margin: 0 0 16px; color: #f1f5f9; }
        .hours-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 14px; }
        .hours-row:last-child { border-bottom: none; }
        .hours-day { color: #94a3b8; }
        .hours-time { font-weight: 500; color: #e2e8f0; }
        .online-badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(34,197,94,0.15); border: 1px solid rgba(34,197,94,0.3); color: #4ade80; border-radius: 99px; padding: 4px 12px; font-size: 12px; font-weight: 600; margin-bottom: 16px; }
        .pulse { width: 7px; height: 7px; border-radius: 50%; background: #22c55e; animation: pulse 1.8s infinite; }
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.4)} }

        /* FORM CARD */
        .form-card {
          background: #fff;
          border-radius: 20px;
          padding: 40px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 24px rgba(0,0,0,0.06);
        }
        @media (max-width: 480px) { .form-card { padding: 24px 20px; } }
        .form-card h2 { font-family: 'Bricolage Grotesque', sans-serif; font-size: 26px; font-weight: 800; color: #0f172a; margin: 0 0 6px; letter-spacing: -0.02em; }
        .form-card p { color: #64748b; font-size: 15px; margin: 0 0 28px; }

        .field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 18px; }
        .field label { font-size: 13px; font-weight: 500; color: #374151; }
        .field input,
        .field textarea,
        .field select {
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          padding: 11px 14px;
          font-size: 15px;
          font-family: 'DM Sans', sans-serif;
          color: #0f172a;
          outline: none;
          transition: border-color 0.18s, box-shadow 0.18s;
          background: #f8fafc;
          appearance: none;
        }
        .field input:focus,
        .field textarea:focus,
        .field select:focus {
          border-color: #1D6FF2;
          box-shadow: 0 0 0 3px rgba(29,111,242,0.12);
          background: #fff;
        }
        .field textarea { min-height: 130px; resize: vertical; }
        .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        @media (max-width: 540px) { .row2 { grid-template-columns: 1fr; } }

        .submit-btn {
          width: 100%;
          background: #000000;
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 14px 24px;
          font-size: 15px;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;
          box-shadow: 0 4px 16px rgba(29,111,242,0.35);
          letter-spacing: 0.01em;
        }
        .submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(29,111,242,0.4); }
        .submit-btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }

        /* Spinner */
        .spinner { width: 18px; height: 18px; border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* SUCCESS */
        .success-state { text-align: center; padding: 48px 24px; }
        .success-icon { width: 72px; height: 72px; background: linear-gradient(135deg, #dcfce7, #bbf7d0); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }
        .success-state h3 { font-family: 'Bricolage Grotesque', sans-serif; font-size: 24px; font-weight: 800; color: #0f172a; margin: 0 0 8px; }
        .success-state p { color: #64748b; font-size: 15px; line-height: 1.6; }
        .success-reset { margin-top: 24px; background: #f1f5f9; border: none; border-radius: 10px; padding: 11px 24px; font-size: 14px; font-family: 'DM Sans', sans-serif; font-weight: 500; color: #1D6FF2; cursor: pointer; }

        /* REVEAL ANIMATION */
        .reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.6s cubic-bezier(.22,1,.36,1), transform 0.6s cubic-bezier(.22,1,.36,1); }
        .reveal.visible { opacity: 1; transform: none; }
        .reveal-delay-1 { transition-delay: 0.1s; }
        .reveal-delay-2 { transition-delay: 0.2s; }
        .reveal-delay-3 { transition-delay: 0.3s; }

        /* FAQ */
        .faq-section { max-width: 740px; margin: 0 auto; padding: 0 24px 80px; }
        .faq-section h2 { font-family: 'Bricolage Grotesque', sans-serif; font-size: 32px; font-weight: 800; color: #0f172a; text-align: center; margin: 0 0 40px; letter-spacing: -0.02em; }
        .faq-item { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; margin-bottom: 12px; overflow: hidden; transition: box-shadow 0.2s; }
        .faq-item:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.07); }
        .faq-q { display: flex; justify-content: space-between; align-items: center; padding: 18px 22px; cursor: pointer; font-weight: 600; font-size: 15px; color: #0f172a; user-select: none; }
        .faq-icon { width: 24px; height: 24px; border-radius: 50%; background: #eff6ff; color: #1D6FF2; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 16px; transition: transform 0.2s; }
        .faq-icon.open { transform: rotate(45deg); }
        .faq-a { padding: 0 22px; max-height: 0; overflow: hidden; transition: max-height 0.3s ease, padding 0.3s ease; color: #64748b; font-size: 14.5px; line-height: 1.65; }
        .faq-a.open { max-height: 200px; padding: 0 22px 18px; }
        .faq-category { font-family: 'Bricolage Grotesque', sans-serif; font-size: 14px; font-weight: 700; color: #1D6FF2; text-transform: uppercase; letter-spacing: 0.05em; margin: 32px 0 12px; }
        .faq-category:first-child { margin-top: 0; }

        /* SOCIAL BAR */
        .social-bar { background: #000000; padding: 48px 24px; text-align: center; }
        .social-bar h3 { font-family: 'Bricolage Grotesque', sans-serif; font-size: 22px; font-weight: 700; color: #f1f5f9; margin: 0 0 8px; }
        .social-bar p { color: #64748b; font-size: 14px; margin: 0 0 28px; }
        .social-links { display: flex; justify-content: center; gap: 16px; }
        .social-link { width: 48px; height: 48px; border-radius: 12px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.05); display: flex; align-items: center; justify-content: center; color: #94a3b8; text-decoration: none; transition: background 0.2s, color 0.2s, border-color 0.2s; }
        .social-link:hover { background: rgba(29,111,242,0.2); color: #60a5fa; border-color: rgba(29,111,242,0.4); }
      `}</style>

      <div className="cpage">
        {/* ── HERO ── */}
        <div
          className="hero"
          ref={heroReveal.ref}
          style={{ opacity: heroReveal.visible ? 1 : 0, transition: "opacity 0.7s ease" }}
        >
          <div className="hero-dot-grid" />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="hero-badge"><MessageSquare size={14} /> We're here to help, every day of the week</div>
            <h1>Get in <span>Touch</span></h1>
            <p>Whether you're a job seeker, employer, or just curious — our team is here and ready to help you succeed.</p>
            <div className="stat-row">
              <div className="stat-pill">
                <span className="dot" style={{ background: "#1D6FF2" }} />
                <div><strong>&lt; 2hr</strong> avg response</div>
              </div>
              <div className="stat-pill">
                <span className="dot" style={{ background: "#22c55e" }} />
                <div><strong>24 / 7</strong> email support</div>
              </div>
              <div className="stat-pill">
                <span className="dot" style={{ background: "#f59e0b" }} />
                <div><strong>98%</strong> satisfaction rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── MAIN GRID ── */}
        <div className="main-grid">
          {/* Info column */}
          <div className="info-col">
            {INFO.map((item, i) => (
              <a
                key={i}
                href={item.href}
                className={`info-card reveal reveal-delay-${i + 1} ${formReveal.visible ? "visible" : ""}`}
                ref={i === 0 ? formReveal.ref : undefined}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
              >
                <div className="info-icon" style={{ background: item.color }}>
                  {item.icon}
                </div>
                <div>
                  <div className="info-label">{item.label}</div>
                  <div className="info-value">{item.value}</div>
                  <div className="info-sub">{item.sub}</div>
                </div>
                <ArrowRight size={16} style={{ color: "#cbd5e1", marginLeft: "auto", alignSelf: "center", flexShrink: 0 }} />
              </a>
            ))}

            {/* Hours card */}
            <div className="hours-card">
              <div className="online-badge"><span className="pulse" /> Online now</div>
              <h3><Clock size={15} style={{ display: "inline", marginRight: 6, verticalAlign: -2 }} />Support hours</h3>
              <div className="hours-row"><span className="hours-day">Monday – Friday</span><span className="hours-time">9 AM – 6 PM IST</span></div>
              <div className="hours-row"><span className="hours-day">Saturday</span><span className="hours-time">10 AM – 4 PM IST</span></div>
              <div className="hours-row"><span className="hours-day">Sunday</span><span className="hours-time">Closed</span></div>
            </div>
          </div>

          {/* Form card */}
          <div className="form-card">
            {submitted ? (
              <div className="success-state">
                <div className="success-icon">
                  <CheckCircle2 size={36} color="#16a34a" />
                </div>
                <h3>Message sent!</h3>
                <p>Thanks for reaching out. Our team will get back to you within 24 hours. Check your inbox — and maybe your spam folder, just in case.</p>
                <button className="success-reset" onClick={() => { setSubmitted(false); setForm({ name: "", email: "", topic: "", message: "" }); }}>
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <h2>Send us a message</h2>
                <p>Fill in the form and we'll get back to you as soon as possible.</p>

                <form onSubmit={handleSubmit}>
                  <div className="row2">
                    <div className="field">
                      <label htmlFor="name">Your name</label>
                      <input id="name" name="name" type="text" placeholder="Vivek Bhatkande" value={form.name} onChange={handleChange} required />
                    </div>
                    <div className="field">
                      <label htmlFor="email">Email address</label>
                      <input id="email" name="email" type="email" placeholder="vivek.bhk@gmail.com" value={form.email} onChange={handleChange} required />
                    </div>
                  </div>

                  <div className="field">
                    <label htmlFor="topic">Topic</label>
                    <select id="topic" name="topic" value={form.topic} onChange={handleChange} required>
                      <option value="" disabled>Select a topic…</option>
                      {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div className="field">
                    <label htmlFor="message">Your message</label>
                    <textarea id="message" name="message" placeholder="Tell us what's on your mind. The more detail, the faster we can help." value={form.message} onChange={handleChange} required />
                  </div>

                  <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? (
                      <><span className="spinner" /> Sending…</>
                    ) : (
                      <><Send size={16} /> Send Message</>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* ── FAQ ── */}
        <div
          className="faq-section"
          ref={faqReveal.ref}
        >
          <h2
            className={`reveal ${faqReveal.visible ? "visible" : ""}`}
            style={{ transitionDelay: "0s" }}
          >
            Frequently asked questions
          </h2>
          {Object.entries(faqsByCategory).map(([category, items]) => (
            <React.Fragment key={category}>
              <div className={`faq-category reveal ${faqReveal.visible ? "visible" : ""}`}>{category}</div>
              {items.map((faq, i) => (
                <div
                  key={faq.id}
                  className={`faq-item reveal reveal-delay-${Math.min(i + 1, 3)} ${faqReveal.visible ? "visible" : ""}`}
                >
                  <div className="faq-q" onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}>
                    {faq.question}
                    <span className={`faq-icon ${openFaq === faq.id ? "open" : ""}`}>+</span>
                  </div>
                  <div className={`faq-a ${openFaq === faq.id ? "open" : ""}`}>{faq.answer}</div>
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>

        {/* ── App install promo ── */}
        <div className="max-w-3xl mx-auto px-6 pb-16">
          <PwaPromoBanner />
        </div>

        {/* ── SOCIAL BAR ── */}
        <div className="social-bar">
          <h3>Find us on social media</h3>
          <p>Stay up to date with the latest jobs, features, and hiring tips</p>
          <div className="social-links">
            <a href="#" className="social-link" aria-label="LinkedIn"><Linkedin size={20} /></a>
            <a href="#" className="social-link" aria-label="Twitter"><Twitter size={20} /></a>
            <a href="#" className="social-link" aria-label="GitHub"><Github size={20} /></a>
            <a href="mailto:queries@jobdew.com" className="social-link" aria-label="Email"><Mail size={20} /></a>
            <a href="#" className="social-link" aria-label="Support"><Headphones size={20} /></a>
          </div>
        </div>
      </div>
    </>
  );
}